import { useEffect, useState } from "react";
import axios from "axios";
import { organization, content, branding } from "../../config";

export default function ResourcesPublic() {
    const [resources, setResources] = useState([]);
    const [logo, setLogo] = useState({ image: "", text: "" });
    const [loading, setLoading] = useState(true);
    const [filterTag, setFilterTag] = useState("all");

    useEffect(() => {
        document.title = `Resources - ${organization.name}`;
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [resourcesRes, logoRes] = await Promise.all([
                    axios.get("/api/public/resources"),
                    axios.get("/api/public/logo")
                ]);
                setResources(resourcesRes.data.content || []);
                setLogo(logoRes.data.content || { image: "", text: "" });
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <div className="container my-5 text-center">
                <p>Loading...</p>
            </div>
        );
    }

    // Get all unique tags
    const allTags = [...new Set(resources.map(r => r.tag).filter(tag => tag))].sort();
    
    // Filter resources by tag
    const filteredResources = filterTag === "all" 
        ? resources 
        : resources.filter(r => r.tag === filterTag);

    return (
        <section
            className="container my-5"
            style={{
                fontFamily: "'Montserrat', sans-serif",
                padding: "40px",
                borderRadius: "12px",
            }}
        >
            {/* Filter Section */}
            {allTags.length > 0 && (
                <div 
                    className="mb-4 p-3"
                    style={{
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "15px",
                        flexWrap: "wrap",
                    }}
                >
                    <span style={{ fontWeight: "600" }}>Filter by:</span>
                    <button
                        className={`btn ${filterTag === "all" ? "btn-admin" : "btn-admin-outline"}`}
                        style={{
                            borderRadius: "20px",
                            padding: "8px 20px",
                            fontWeight: "500",
                            fontSize: "0.9rem",
                        }}
                        onClick={() => setFilterTag("all")}
                    >
                        All ({resources.length})
                    </button>
                    {allTags.map(tag => (
                        <button
                            key={tag}
                            className={`btn ${filterTag === tag ? "btn-admin" : "btn-admin-outline"}`}
                            style={{
                                borderRadius: "20px",
                                padding: "8px 20px",
                                fontWeight: "500",
                                fontSize: "0.9rem",
                            }}
                            onClick={() => setFilterTag(tag)}
                        >
                            {tag} ({resources.filter(r => r.tag === tag).length})
                        </button>
                    ))}
                </div>
            )}

            {/* Resources Grid */}
            {filteredResources.length === 0 ? (
                <div className="alert alert-info text-center">
                    No resources found.
                </div>
            ) : (
                <div className="row g-4">
                    {filteredResources.map((resource, index) => {
                        const isPDF = resource.image?.toLowerCase().endsWith('.pdf');
                        return (
                            <div key={index} className="col-12 col-md-6 col-lg-4">
                                <a
                                    href={resource.image}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: "none" }}
                                >
                                    <div
                                        className="card h-100 text-center"
                                        style={{
                                            border: "none",
                                            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                                            borderRadius: "12px",
                                            overflow: "hidden",
                                            transition: "transform 0.2s, box-shadow 0.2s",
                                            cursor: "pointer",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "translateY(-5px)";
                                            e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 0, 0, 0.15)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow = "0 4px 10px rgba(0, 0, 0, 0.1)";
                                        }}
                                    >
                                        {isPDF ? (
                                            <div
                                                style={{
                                                    height: "200px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: "white",
                                                    padding: "30px",
                                                }}
                                            >
                                                <img
                                                    src={logo.image || ""}
                                                    alt="PDF Resource"
                                                    loading={index < 3 ? "eager" : "lazy"}
                                                    style={{
                                                        maxWidth: "100%",
                                                        maxHeight: "100%",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            </div>
                                        ) : resource.image ? (
                                            <img
                                                src={resource.image}
                                                alt={resource.label}
                                                className="card-img-top"
                                                loading={index < 3 ? "eager" : "lazy"}
                                                style={{
                                                    height: "200px",
                                                    objectFit: "cover",
                                                    objectPosition: "center",
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    height: "200px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: "#006699",
                                                    padding: "30px",
                                                }}
                                            >
                                                <img
                                                    src={logo.image || ""}
                                                    alt="Resource"
                                                    style={{
                                                        maxWidth: "100%",
                                                        maxHeight: "100%",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div className="card-body" style={{ padding: "20px" }}>
                                            <h5
                                                className="card-title"
                                                style={{
                                                    fontWeight: "700",
                                                    marginBottom: "10px",
                                                    fontSize: "1.1rem",
                                                    color: "#333",
                                                }}
                                            >
                                                {resource.label}
                                            </h5>
                                            {resource.tag && (
                                                <span
                                                    className="badge"
                                                    style={{
                                                        backgroundColor: branding.primaryColor,
                                                        fontSize: "0.75rem",
                                                        padding: "5px 10px",
                                                    }}
                                                >
                                                    {resource.tag}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </a>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}
